// src/components/AdminDashboard.js (Updated with Bulk ID Card Generator)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Avatar,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  TablePagination,
  ListItemIcon,
  Menu,
  ListItemText,
  Snackbar,
  List,
  ListItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CancelOutlined as XCircle } from '@mui/icons-material';
import {
  UserPlus,
  Users,
  Edit3,
  Trash2,
  Search,
  RefreshCcw,
  Filter,
  Download,
  FileText,
  Layers,
  Mail,
  Copy,
  MoreVertical,
  CheckCircle,
  Lock,
  Unlock,
  UserCheck,
  Eye,
  EyeOff,
  Group,
  Upload,
  Send,
  Check,
  UserMinus,
  CreditCard,
  File
} from 'lucide-react';
import Papa from 'papaparse';
import adminService from './AdminService';
import CompanyManagementTab from '../CompanyManagementTab';
import BulkIDCardGenerator from './BulkIDCardGenerator';
import { Building2 } from 'lucide-react';

// TabPanel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
      sx={{
        display: value !== index ? 'none' : 'block',
        p: 3
      }}
    >
      {value === index && children}
    </Box>
  );
};

const AdminDashboard = () => {
  // State for users and exports
  const [users, setUsers] = useState([]);
  const [exports, setExports] = useState([]);
  const [idCardExports, setIdCardExports] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inviteSuccessDialogOpen, setInviteSuccessDialogOpen] = useState(false);
  const [bulkIdCardDialogOpen, setBulkIdCardDialogOpen] = useState(false);

  // Selected user state
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [newUserData, setNewUserData] = useState({ name: '', email: '', role: 'user' });
  const [invitedUserData, setInvitedUserData] = useState(null);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');
  const [companies, setCompanies] = useState([]);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // CSV upload dialog
  const [bulkInviteDialogOpen, setBulkInviteDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvParseResults, setCsvParseResults] = useState(null);
  const [csvUploadError, setCsvUploadError] = useState('');
  const [bulkInviteLoading, setBulkInviteLoading] = useState(false);

  // Fetch all users and exports when the component mounts
  useEffect(() => {
    fetchUsers();
    fetchExports();
    loadIdCardExports();
  }, []);

  const navigate = useNavigate();

  // Load ID card exports from localStorage
  const loadIdCardExports = () => {
    try {
      const savedExports = JSON.parse(localStorage.getItem('admin_id_card_exports') || '[]');
      setIdCardExports(savedExports);
    } catch (error) {
      console.error('Error loading ID card exports:', error);
    }
  };

  // Get current user data from localStorage
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  };

  // Extract domain from email
  const extractDomain = (email) => {
    if (!email) return '';
    return email.split('@')[1]?.toLowerCase() || '';
  };

  // Format users data with default values for missing fields and domain filtering
  const formatUsers = (usersData) => {
    const currentUser = getCurrentUser();
    
    // First format the users
    const formattedUsers = usersData.map(user => ({
      ...user,
      name: user.name || 'No Name Set',
      profile_visibility: user.profile_visibility || 'private',
      role: user.role || 'user',
      company: user.company || 'Not Specified',
      created_at: user.created_at || new Date().toISOString()
    }));

    // Apply domain filtering based on current user's role
    if (!currentUser) {
      console.warn('No current user found in localStorage');
      return [];
    }

    // If user role is 'user', show nothing
    if (currentUser.role === 'user') {
      return [];
    }

    // If user role is 'superadmin', show all users
    if (currentUser.role === 'superadmin') {
      return formattedUsers;
    }

    // If user role is 'admin', show only users from same domain
    if (currentUser.role === 'admin') {
      const currentUserDomain = extractDomain(currentUser.email);
      if (!currentUserDomain) {
        console.warn('Could not extract domain from current user email');
        return [];
      }

      return formattedUsers.filter(user => {
        const userDomain = extractDomain(user.email);
        return userDomain === currentUserDomain;
      });
    }

    return [];
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getAllUsers();
      const formattedUsers = formatUsers(data || []);
      setUsers(formattedUsers);

      // Extract and set unique companies
      const companyList = extractUniqueCompanies(formattedUsers);
      setCompanies(companyList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch exports from API
  const fetchExports = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getAllExports();
      setExports(data || []);
    } catch (err) {
      console.error('Error fetching exports:', err);
      setError('Failed to fetch exports: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk ID card generation complete
const handleBulkIdCardComplete = () => {
  loadIdCardExports();
  // Read the latest cards from localStorage
const saved = JSON.parse(localStorage.getItem('admin_id_card_exports') || '[]');
const count = Array.isArray(saved) ? saved.length : 0;
  setSnackbar({
    open: true,
    message: `Successfully generated ${count} ID card${count === 1 ? '' : 's'}`,
    severity: 'success'
  });
};

  // Download ID card PDF
  const handleDownloadIdCard = (exportItem) => {
    try {
      if (exportItem.pdf_data) {
        // Convert data URL to blob
        const byteCharacters = atob(exportItem.pdf_data.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = exportItem.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setSnackbar({
          open: true,
          message: 'ID card downloaded successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error downloading ID card:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download ID card',
        severity: 'error'
      });
    }
  };

  // Delete ID card export
  const handleDeleteIdCardExport = (exportId) => {
    try {
      const updatedExports = idCardExports.filter(exp => exp.id !== exportId);
      localStorage.setItem('admin_id_card_exports', JSON.stringify(updatedExports));
      setIdCardExports(updatedExports);

      setSnackbar({
        open: true,
        message: 'ID card export deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting ID card export:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete ID card export',
        severity: 'error'
      });
    }
  };

  // Open user menu
  const handleOpenMenu = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  // Close user menu
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  // Open edit dialog
  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role || 'user');
    setEditDialogOpen(true);
    handleCloseMenu();
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setSelectedRole('');
  };

  // Open delete dialog
  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // Open invite dialog
  const handleOpenInviteDialog = () => {
    setNewUserData({ name: '', email: '', role: 'user' });
    setInviteDialogOpen(true);
  };

  // Close invite dialog
  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setNewUserData({ name: '', email: '', role: 'user' });
  };

  // Update user role
  const handleUpdateRole = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError('');

    try {
      const updatedUser = await adminService.updateUserRole(selectedUser.id, selectedRole);

      // Update the user in the list
      setUsers(users.map(u =>
        u.id === updatedUser.id ? { ...u, role: updatedUser.role } : u
      ));

      setSnackbar({
        open: true,
        message: `User role updated to ${updatedUser.role}`,
        severity: 'success'
      });

      handleCloseEditDialog();
    } catch (err) {
      setError('Failed to update role: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError('');

    try {
      await adminService.deleteUser(selectedUser.id);

      // Remove the user from the list
      setUsers(users.filter(u => u.id !== selectedUser.id));

      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });

      handleCloseDeleteDialog();
    } catch (err) {
      setError('Failed to delete user: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Invite new user
  const handleInviteUser = async () => {
    setLoading(true);
    setError('');

    try {
      // Call the updated service method that sends an email invitation
      const result = await adminService.inviteUserEmail(newUserData);
      console.log("Invited user result:", result);

      // Add the new user to the list
      const newUser = {
        id: result.id,
        name: result.name || newUserData.name || 'No Name Set',
        email: result.email || newUserData.email,
        role: result.role || newUserData.role || 'user',
        profile_visibility: 'private',
        skills_count: 0,
        languages_count: 0,
        created_at: new Date().toISOString()
      };

      setUsers([newUser, ...users]);

      // Store invited user data for success dialog
      setInvitedUserData(newUser);

      handleCloseInviteDialog();
      setInviteSuccessDialogOpen(true);
    } catch (err) {
      setError('Failed to invite user: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter users based on search term and role filter
  const filteredUsers = users.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (user.name?.toString() || '').toLowerCase().includes(searchTermLower) ||
      (user.email?.toString() || '').toLowerCase().includes(searchTermLower) ||
      (user.role?.toString() || '').toLowerCase().includes(searchTermLower) ||
      (user.company?.toString() || '').toLowerCase().includes(searchTermLower)
    );

    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesCompany = filterCompany === 'all' || user.company === filterCompany;

    return matchesSearch && matchesRole && matchesCompany;
  });

  // Paginate users
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Tab IDs
  const a11yProps = (index) => ({
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  });

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: 'Copied to clipboard',
      severity: 'success'
    });
  };

  const handleOpenBulkInviteDialog = () => {
    setCsvFile(null);
    setCsvParseResults(null);
    setCsvUploadError('');
    setBulkInviteDialogOpen(true);
  };

  const handleCloseBulkInviteDialog = () => {
    setBulkInviteDialogOpen(false);
    setCsvFile(null);
    setCsvParseResults(null);
    setCsvUploadError('');
  };

  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      setCsvUploadError('');

      // Parse the CSV file to preview the data
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvData = e.target.result;
          Papa.parse(csvData, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              // Validate the CSV structure
              const requiredColumns = ['email', 'name', 'role'];
              const headers = Object.keys(results.data[0]);

              const missingColumns = requiredColumns.filter(
                col => !headers.map(h => h.toLowerCase()).includes(col.toLowerCase())
              );

              if (missingColumns.length > 0) {
                setCsvUploadError(`CSV is missing required columns: ${missingColumns.join(', ')}`);
                setCsvParseResults(null);
              } else {
                // Validate data and convert to required format
                const processedData = results.data.map(row => ({
                  email: row.email ? row.email.trim() : '',
                  name: row.name ? row.name.trim() : '',
                  role: ['admin', 'user'].includes(row.role?.toLowerCase().trim())
                    ? row.role.toLowerCase().trim()
                    : 'user'
                }));

                // Check for missing emails
                const invalidRows = processedData.filter(row => !row.email);
                if (invalidRows.length > 0) {
                  setCsvUploadError(`CSV contains ${invalidRows.length} row(s) with missing email addresses`);
                }

                setCsvParseResults({
                  data: processedData,
                  totalUsers: processedData.length,
                  validUsers: processedData.filter(row => row.email).length
                });
              }
            },
            error: (error) => {
              setCsvUploadError(`Error parsing CSV: ${error.message}`);
              setCsvParseResults(null);
            }
          });
        } catch (error) {
          setCsvUploadError(`Error reading file: ${error.message}`);
          setCsvParseResults(null);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleBulkInvite = async () => {
    if (!csvParseResults || csvParseResults.validUsers === 0) {
      setCsvUploadError('No valid users to invite');
      return;
    }

    setBulkInviteLoading(true);
    setCsvUploadError('');

    try {
      // Filter out invalid entries
      const usersToInvite = csvParseResults.data.filter(user => user.email);

      // Send the bulk invite request
      const result = await adminService.bulkInviteUsers(usersToInvite);

      // Add the new users to the list
      const newUsers = result.users.map(user => ({
        id: user.id,
        name: user.name || 'No Name Set',
        email: user.email,
        role: user.role || 'user',
        profile_visibility: 'private',
        skills_count: 0,
        languages_count: 0,
        created_at: new Date().toISOString()
      }));

      setUsers([...newUsers, ...users]);

      setSnackbar({
        open: true,
        message: `Successfully sent invitations to ${result.successCount} users`,
        severity: 'success'
      });

      handleCloseBulkInviteDialog();
    } catch (err) {
      console.error('Error bulk inviting users:', err);
      setCsvUploadError('Failed to send invitations: ' + (err.response?.data?.error || err.message));
    } finally {
      setBulkInviteLoading(false);
    }
  };

  const extractUniqueCompanies = (usersData) => {
    const uniqueCompanies = new Set();

    // Add 'All Companies' option
    uniqueCompanies.add('all');

    // Extract company names from users
    usersData.forEach(user => {
      if (user.company && user.company.trim() !== '') {
        uniqueCompanies.add(user.company.trim());
      }
    });

    return Array.from(uniqueCompanies);
  };

  const handleToggleVisibility = async (user) => {
    handleCloseMenu(); // Close the menu first

    // Determine the new status - opposite of the current status
    const newStatus = user.profile_visibility === 'enabled' ? 'disabled' : 'enabled';

    setLoading(true);
    setError('');

    try {
      // Call the API to update the status
      const result = await adminService.updateUserStatus(user.id, newStatus);

      // Update the user in the list
      setUsers(users.map(u =>
        u.id === user.id ? { ...u, profile_visibility: newStatus } : u
      ));

      // Show success message
      setSnackbar({
        open: true,
        message: result.message || `User profile status ${newStatus}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating user status:', err);

      // Show error message
      setSnackbar({
        open: true,
        message: `Failed to update status: ${err.response?.data?.error || err.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdminRole = async (user) => {
    // Close the menu
    handleCloseMenu();

    // Determine the new role (toggle between 'admin' and 'user')
    const newRole = user.role === 'admin' ? 'user' : 'admin';

    setLoading(true);
    setError('');

    try {
      const updatedUser = await adminService.updateUserRole(user.id, newRole);

      // Update the user in the list
      setUsers(users.map(u =>
        u.id === updatedUser.id ? { ...u, role: updatedUser.role } : u
      ));

      setSnackbar({
        open: true,
        message: newRole === 'admin'
          ? `Admin access enabled for ${user.name || user.email}`
          : `Admin access disabled for ${user.name || user.email}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update role: ' + (err.response?.data?.error || err.message));

      setSnackbar({
        open: true,
        message: `Failed to update role: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUserProfile = (user) => {
    handleCloseMenu();
    navigate(`/profiles/admin/edit-user/${user.id}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box className="max-w-7xl mx-auto p-6">
      <Grid container spacing={3} className="mb-6">
        {/* Users Stats Card */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              borderRadius: 2,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 8,
              },
            }}
          >
            <CardContent>
              <Box className="flex items-center mb-4">
                <Avatar sx={{ bgcolor: '#2563eb', mr: 2 }}>
                  <Users size={24} />
                </Avatar>
                <Typography variant="h6" className="font-semibold">
                  Total Users
                </Typography>
              </Box>
              <Typography variant="h3" className="mb-2 font-bold">
                {users.length}
              </Typography>
              <Box className="flex items-center justify-between">
                <Typography variant="body2" color="textSecondary">
                  {users.filter(u => u.role === 'admin').length} Admins
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {users.filter(u => u.role !== 'admin').length} Regular Users
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Exports Stats Card */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              borderRadius: 2,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 8,
              },
            }}
          >
            <CardContent>
              <Box className="flex items-center mb-4">
                <Avatar sx={{ bgcolor: '#10B981', mr: 2 }}>
                  <FileText size={24} />
                </Avatar>
                <Typography variant="h6" className="font-semibold">
                  Profile Exports
                </Typography>
              </Box>
              <Typography variant="h3" className="mb-2 font-bold">
                {exports.length + idCardExports.length}
              </Typography>
              <Box className="flex items-center justify-between">
                <Typography variant="body2" color="textSecondary">
                  {exports.filter(e => e.format === 'html').length} HTML
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {idCardExports.length} ID Cards
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions Card */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              borderRadius: 2,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 8,
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Quick Actions
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UserPlus />}
                onClick={handleOpenInviteDialog}
                fullWidth
                className="mb-3"
              >
                Invite New User
              </Button>
              <Button
                variant="outlined"
                startIcon={<Group />}
                onClick={handleOpenBulkInviteDialog}
                fullWidth
                className="mb-3"
              >
                Bulk Invite Users
              </Button>
              <Button
                variant="outlined"
                startIcon={<CreditCard />}
                onClick={() => setBulkIdCardDialogOpen(true)}
                fullWidth
                className="mb-3"
                sx={{ 
                  color: '#7c3aed', 
                  borderColor: '#7c3aed',
                  '&:hover': {
                    borderColor: '#5b21b6',
                    backgroundColor: 'rgba(124, 58, 237, 0.04)'
                  }
                }}
              >
                Bulk ID Card Generator
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshCcw />}
                onClick={() => {
                  fetchUsers();
                  fetchExports();
                  loadIdCardExports();
                }}
                fullWidth
              >
                Refresh All Data
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={3} className="rounded-xl overflow-hidden mb-6">
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            backgroundColor: '#f8fafc',
            '& .MuiTab-root': { py: 2 }
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ mr: 1 }}>User Management</Box>
                <Users className="w-4 h-4" />
              </Box>
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ mr: 1 }}>Export Tracking</Box>
                <FileText className="w-4 h-4" />
              </Box>
            }
            {...a11yProps(1)}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ mr: 1 }}>Company Management</Box>
                <Building2 className="w-4 h-4" />
              </Box>
            }
            {...a11yProps(2)}
          />
        </Tabs>

        {/* User Management Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3} className="mb-4">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search users by name, email or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search className="w-5 h-5 mr-2 text-gray-400" />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  label="Filter by Role"
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin Only</MenuItem>
                  <MenuItem value="user">Users Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Company</InputLabel>
                <Select
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  label="Filter by Company"
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  {companies.filter(c => c !== 'all').map((company) => (
                    <MenuItem key={company} value={company}>
                      {company}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Show domain filtering status */}
          {(() => {
            const currentUser = getCurrentUser();
            if (currentUser && currentUser.role === 'admin') {
              const domain = extractDomain(currentUser.email);
              return (
                <Alert severity="info" className="mb-4">
                  <Typography variant="body2">
                    <strong>Domain Filter Active:</strong> Showing only users from domain "{domain}" 
                    ({filteredUsers.length} users found)
                  </Typography>
                </Alert>
              );
            } else if (currentUser && currentUser.role === 'superadmin') {
              return (
                <Alert severity="success" className="mb-4">
                  <Typography variant="body2">
                    <strong>Super Admin Access:</strong> Showing all users across all domains 
                    ({users.length} total users)
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}

          {loading && !users.length ? (
            <Box className="flex justify-center p-10">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell>User Info</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <TableRow
                        key={user.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f9fafb',
                          }
                        }}
                      >
                        <TableCell>
                          <Box className="flex items-center">
                            <Avatar
                              sx={{
                                bgcolor: user.role === 'admin' ? '#2563eb' : '#64748b',
                                mr: 2
                              }}
                            >
                              {user.name ? user.name[0].toUpperCase() : user.email ? user.email[0].toUpperCase() : 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" className="font-medium">
                                {user.name || 'No Name Set'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatDate(user.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className="flex items-center">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            <Typography variant="body2">{user.email}</Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={user.role || 'user'}
                            color={user.role === 'admin' ? 'primary' : 'default'}
                            size="small"
                            icon={user.role === 'admin' ? <UserCheck className="w-4 h-4" /> : undefined}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={user.profile_visibility === 'enabled' ? 'Enabled' : 'Disabled'}
                            color={user.profile_visibility === 'enabled' ? 'success' : 'default'}
                            size="small"
                            icon={user.profile_visibility === 'enabled' ?
                              <CheckCircle className="w-4 h-4" /> :
                              <XCircle className="w-4 h-4" />
                            }
                            sx={{
                              backgroundColor: user.profile_visibility === 'enabled' ? 'rgba(46, 204, 113, 0.1)' : 'rgba(189, 195, 199, 0.1)',
                              '& .MuiChip-icon': {
                                color: user.profile_visibility === 'enabled' ? '#2ecc71' : '#95a5a6'
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {user.company || 'Not Specified'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => handleOpenMenu(e, user)}
                          >
                            <MoreVertical className="w-5 h-5" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box className="py-8">
                          <Typography variant="subtitle1" className="mb-2 font-medium">
                            No users found
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {searchTerm ? 'Try a different search term' : 'Invite new users to get started'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </TabPanel>

        {/* Export Tracking Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box className="mb-4">
            <Typography variant="h6" className="mb-2 font-medium">
              Export History
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Track and manage all profile exports and ID cards across your organization.
            </Typography>
          </Box>

          {/* ID Card Exports Section */}
          {idCardExports.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard size={20} />
                ID Card Exports ({idCardExports.length})
              </Typography>
              
              <TableContainer component={Paper} elevation={2} sx={{ mb: 3 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Date & Time</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {idCardExports.map((exp) => (
                      <TableRow key={exp.id} hover>
                        <TableCell>
                          <Box className="flex items-center">
                            <Avatar
                              sx={{ bgcolor: '#7c3aed', mr: 2, width: 32, height: 32 }}
                            >
                              {exp.user_name ? exp.user_name[0].toUpperCase() : 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">
                                {exp.user_name || `User ID: ${exp.user_id}`}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {exp.user_email || ''}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <File size={16} />
                            <Typography variant="body2">
                              {exp.filename}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(exp.file_size || 0)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(exp.created_at)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {exp.created_at ? new Date(exp.created_at).toLocaleTimeString() : 'N/A'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Download ID Card">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleDownloadIdCard(exp)}
                              sx={{ mr: 1 }}
                            >
                              <Download className="w-5 h-5" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Export">
                            <IconButton 
                              color="error" 
                              onClick={() => handleDeleteIdCardExport(exp.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Regular Profile Exports */}
          {loading && !exports.length ? (
            <Box className="flex justify-center p-10">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {exports.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileText size={20} />
                    Profile Exports ({exports.length})
                  </Typography>
                  
                  <TableContainer component={Paper} elevation={2}>
                    <Table>
                      <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Format</TableCell>
                          <TableCell>Date & Time</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exports.map((exp) => (
                          <TableRow key={exp.id} hover>
                            <TableCell>
                              <Box className="flex items-center">
                                <Avatar
                                  sx={{ bgcolor: '#64748b', mr: 2, width: 32, height: 32 }}
                                >
                                  {exp.user_name ? exp.user_name[0].toUpperCase() : 'U'}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">
                                    {exp.user_name || `Profile ID: ${exp.profile_id}`}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {exp.user_email || ''}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">
                                {exp.filename}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={exp.format?.toUpperCase() || 'UNKNOWN'}
                                color={exp.format === 'html' ? 'primary' : 'secondary'}
                                size="small"
                              />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(exp.created_at)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {exp.created_at ? new Date(exp.created_at).toLocaleTimeString() : 'N/A'}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <IconButton color="primary" disabled>
                                <Download className="w-5 h-5" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {exports.length === 0 && idCardExports.length === 0 && (
                <TableContainer component={Paper} elevation={2}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center">
                          <Box className="py-8">
                            <Typography variant="subtitle1" className="mb-2 font-medium">
                              No exports tracked yet
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Exports will be tracked when users download profile pages or generate ID cards
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </TabPanel>
        
        <TabPanel value={activeTab} index={2}>
          <CompanyManagementTab />
        </TabPanel>
      </Paper>

      {/* User Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            width: 200,
            borderRadius: 2,
            '& .MuiMenuItem-root': {
              py: 1.5
            }
          }
        }}
      >
        {/* Toggling a superadmin would demote them to admin, so it isn't offered */}
        <MenuItem onClick={() => handleToggleAdminRole(menuUser)} disabled={menuUser?.role === 'superadmin'}>
          <ListItemIcon>
            {menuUser?.role === 'admin' ? (
              <UserMinus className="w-5 h-5" color="#f44336" />
            ) : (
              <UserPlus className="w-5 h-5" color="#4caf50" />
            )}
          </ListItemIcon>
          <ListItemText>
            {menuUser?.role === 'superadmin' ? 'Superadmin (fixed)' : menuUser?.role === 'admin' ? 'Disable Admin' : 'Enable Admin'}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleToggleVisibility(menuUser)}>
          <ListItemIcon>
            {menuUser?.profile_visibility === 'enabled' ? (
              <Lock className="w-5 h-5" />
            ) : (
              <Unlock className="w-5 h-5" />
            )}
          </ListItemIcon>
          <ListItemText>
            {menuUser?.profile_visibility === 'enabled' ? 'Disable Profile' : 'Enable Profile'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditUserProfile(menuUser)}>
          <ListItemIcon>
            <Edit3 className="w-5 h-5" color="#2563eb" />
          </ListItemIcon>
          <ListItemText>Edit User Profile</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleOpenDeleteDialog(menuUser)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <Trash2 className="w-5 h-5" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 400, mt: 2 }}>
            <Typography variant="subtitle1" className="mb-4">
              Are you sure you want to delete this user? This action cannot be undone.
            </Typography>

            <Alert severity="warning" className="mb-4">
              All user data, including profile information, skills, languages, and uploaded photos will be permanently deleted.
            </Alert>

            <Box className="p-4 bg-gray-50 rounded-lg">
              <Typography variant="subtitle2" className="font-medium">
                {selectedUser?.name || 'No Name Set'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog}>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 400, mt: 2 }}>
            <Typography variant="body2" color="textSecondary" className="mb-4">
              Invite a new user to join your organization. An email invitation will be sent to the email address provided.
            </Typography>

            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              placeholder="John Doe"
            />

            <TextField
              label="Email Address"
              fullWidth
              margin="normal"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              required
              placeholder="john.doe@example.com"
              type="email"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={newUserData.role}
                onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button
            onClick={handleInviteUser}
            color="primary"
            variant="contained"
            disabled={loading || !newUserData.email}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Success Dialog */}
      <Dialog
        open={inviteSuccessDialogOpen}
        onClose={() => setInviteSuccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#f0fdf4',
          color: '#166534'
        }}>
          <CheckCircle className="w-6 h-6 mr-2" />
          User Invited Successfully
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle1" className="mb-4">
              The following user has been invited:
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Name:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {newUserData.name || 'Not provided'}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Email:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {newUserData.email}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Role:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Chip
                    label={newUserData.role}
                    color={newUserData.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                An invitation email has been sent to {newUserData.email} with instructions to set up their account.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInviteSuccessDialogOpen(false)}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Option CSV */}
      <Dialog
        open={bulkInviteDialogOpen}
        onClose={handleCloseBulkInviteDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Invite Users via CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload a CSV file containing user information to send multiple invitations at once.
              The CSV file must include the columns: email (required), name, and role.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Upload />}
                sx={{ mb: 2 }}
              >
                Upload CSV File
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleCsvFileChange}
                />
              </Button>

              {csvFile && (
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Selected file: {csvFile.name}
                </Typography>
              )}
            </Box>

            {csvUploadError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {csvUploadError}
              </Alert>
            )}

            {csvParseResults && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  CSV Preview:
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {csvParseResults.data.slice(0, 5).map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.email || <Typography color="error">Missing</Typography>}</TableCell>
                            <TableCell>{row.name || 'No Name Set'}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.role || 'user'}
                                size="small"
                                color={row.role === 'admin' ? 'primary' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {csvParseResults.data.length > 5 && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      Showing 5 of {csvParseResults.data.length} entries
                    </Typography>
                  )}
                </Paper>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    Total users in CSV: <strong>{csvParseResults.totalUsers}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Valid users to invite: <strong>{csvParseResults.validUsers}</strong>
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                CSV File Requirements:
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Check fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Must include headers: email, name, role" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Check fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Email is required for each user" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Check fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Role must be either 'admin' or 'user' (defaults to 'user')" />
                </ListItem>
              </List>

              <Button
                size="small"
                startIcon={<Download />}
                sx={{ mt: 1 }}
                onClick={() => {
                  const template = 'email,name,role\njohn@example.com,John Doe,user\nmary@example.com,Mary Smith,admin\n';
                  const blob = new Blob([template], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'bulk_invite_template.csv';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                Download Template
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkInviteDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkInvite}
            variant="contained"
            color="primary"
            disabled={!csvParseResults || csvParseResults.validUsers === 0 || bulkInviteLoading}
            startIcon={bulkInviteLoading ? <CircularProgress size={20} /> : <Send />}
          >
            {bulkInviteLoading ? 'Sending Invitations...' : 'Send Invitations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk ID Card Generator Dialog */}
      <BulkIDCardGenerator
        open={bulkIdCardDialogOpen}
        onClose={() => setBulkIdCardDialogOpen(false)}
        onExportComplete={handleBulkIdCardComplete}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;